import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown, Zap, ChevronRight, X } from 'lucide-react';
import clsx from 'clsx';

// FREE tier limits
export const FREE_LIMITS = {
    boards: 5,
    tasksPerBoard: 100,
    members: 5,
    objectives: 5,
    keyResultsPerObjective: 5,
};

// PRO tier limits
export const PRO_LIMITS = {
    boards: Infinity,
    tasksPerBoard: Infinity,
    members: 20,
    objectives: Infinity,
    keyResultsPerObjective: 10,
};

interface PlanBadgeProps {
    plan?: 'free' | 'pro';
    usage?: {
        boards?: { used: number; limit: number };
        tasks?: { used: number; limit: number };
        members?: { used: number; limit: number };
    };
}

export default function PlanBadge({ plan = 'free', usage }: PlanBadgeProps) {
    const { t } = useTranslation();
    const [showDropdown, setShowDropdown] = useState(false);

    const isPro = plan === 'pro';

    return (
        <div className="relative">
            <button
                onClick={() => setShowDropdown(!showDropdown)}
                className={clsx(
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all',
                    isPro
                        ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-500/20 dark:text-amber-400 dark:hover:bg-amber-500/30'
                        : 'bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-500/20 dark:text-primary-400 dark:hover:bg-primary-500/30'
                )}
            >
                {isPro ? (
                    <>
                        <Crown className="w-3.5 h-3.5" />
                        PRO
                    </>
                ) : (
                    <>
                        <Zap className="w-3.5 h-3.5" />
                        FREE
                    </>
                )}
            </button>

            {/* Dropdown with usage info */}
            {showDropdown && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowDropdown(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 z-50 overflow-hidden">
                        {/* Header */}
                        <div className={clsx(
                            'px-4 py-3 flex items-center justify-between',
                            isPro
                                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                                : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white'
                        )}>
                            <div className="flex items-center gap-2">
                                {isPro ? <Crown className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                                <span className="font-bold">{isPro ? 'VisionBoard PRO' : 'VisionBoard FREE'}</span>
                            </div>
                            <button
                                onClick={() => setShowDropdown(false)}
                                className="p-1 hover:bg-white/20 rounded"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Usage Stats */}
                        {!isPro && usage && (
                            <div className="p-4 space-y-3">
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase font-medium">
                                    {t('plan.usage', 'Mức sử dụng')}
                                </p>

                                {usage.boards && (
                                    <UsageBar
                                        label={t('plan.boards', 'Boards')}
                                        used={usage.boards.used}
                                        limit={usage.boards.limit}
                                    />
                                )}

                                {usage.members && (
                                    <UsageBar
                                        label={t('plan.members', 'Thành viên')}
                                        used={usage.members.used}
                                        limit={usage.members.limit}
                                    />
                                )}
                            </div>
                        )}

                        {/* Upgrade CTA for FREE users */}
                        {!isPro && (
                            <div className="p-4 bg-neutral-50 dark:bg-neutral-700/50 border-t border-neutral-200 dark:border-neutral-700">
                                <Link
                                    to="/pricing"
                                    className="flex items-center justify-between w-full px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all"
                                    onClick={() => setShowDropdown(false)}
                                >
                                    <span className="flex items-center gap-2">
                                        <Crown className="w-4 h-4" />
                                        {t('plan.upgradeToPro', 'Nâng cấp lên PRO')}
                                    </span>
                                    <ChevronRight className="w-4 h-4" />
                                </Link>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2 text-center">
                                    {t('plan.upgradeDesc', 'Mở khóa tất cả tính năng không giới hạn')}
                                </p>
                            </div>
                        )}

                        {/* PRO features list */}
                        {isPro && (
                            <div className="p-4">
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase font-medium mb-2">
                                    {t('plan.proFeatures', 'Tính năng PRO')}
                                </p>
                                <ul className="space-y-1.5 text-sm text-neutral-600 dark:text-neutral-300">
                                    <li>✓ Boards không giới hạn</li>
                                    <li>✓ Tasks không giới hạn</li>
                                    <li>✓ 20 thành viên mỗi workspace</li>
                                    <li>✓ OKRs không giới hạn</li>
                                    <li>✓ Hỗ trợ ưu tiên</li>
                                </ul>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function UsageBar({ label, used, limit }: { label: string; used: number; limit: number }) {
    const percentage = Math.min((used / limit) * 100, 100);
    const isNearLimit = percentage >= 80;
    const isAtLimit = used >= limit;

    return (
        <div>
            <div className="flex justify-between text-sm mb-1">
                <span className="text-neutral-600 dark:text-neutral-300">{label}</span>
                <span className={clsx(
                    'font-medium',
                    isAtLimit
                        ? 'text-danger-600 dark:text-danger-400'
                        : isNearLimit
                            ? 'text-warning-600 dark:text-warning-400'
                            : 'text-neutral-600 dark:text-neutral-300'
                )}>
                    {used}/{limit}
                </span>
            </div>
            <div className="h-2 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden">
                <div
                    className={clsx(
                        'h-full rounded-full transition-all',
                        isAtLimit
                            ? 'bg-danger-500'
                            : isNearLimit
                                ? 'bg-warning-500'
                                : 'bg-primary-500'
                    )}
                    style={{ width: `${percentage}%` }}
                />
            </div>
        </div>
    );
}

// Upgrade Prompt Modal
export function UpgradePrompt({
    show,
    onClose,
    resource,
    used,
    limit,
}: {
    show: boolean;
    onClose: () => void;
    resource: string;
    used: number;
    limit: number;
}) {
    const { t } = useTranslation();

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                    <div className="flex items-center gap-3 text-white">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                            <Crown className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">{t('plan.limitReached', 'Đã đạt giới hạn')}</h3>
                            <p className="text-white/80 text-sm">{t('plan.upgradeToUnlock', 'Nâng cấp để mở khóa')}</p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 text-center">
                    <p className="text-neutral-600 dark:text-neutral-300 mb-4">
                        {t('plan.limitMessage', 'Bạn đã sử dụng {{used}}/{{limit}} {{resource}} trong gói FREE.', { used, limit, resource })}
                    </p>

                    <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl p-4 mb-6">
                        <p className="font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                            {t('plan.proUnlocks', 'VisionBoard PRO mở khóa:')}
                        </p>
                        <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
                            <li>✓ {resource} không giới hạn</li>
                            <li>✓ Tất cả tính năng premium</li>
                            <li>✓ Hỗ trợ ưu tiên</li>
                        </ul>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors font-medium"
                        >
                            {t('common.close', 'Đóng')}
                        </button>
                        <Link
                            to="/pricing"
                            className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-colors font-medium flex items-center justify-center gap-2"
                            onClick={onClose}
                        >
                            <Crown className="w-4 h-4" />
                            {t('plan.upgradeToPro', 'Nâng cấp PRO')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
